import { useSyncExternalStore } from 'react';
import type { Msg, Pair, Vars } from './messages';
import { STRINGS, type MsgKey } from './strings';

export type { MsgKey } from './strings';
export type { Vars } from './messages';

export type Locale = 'ru' | 'en';

/** Endonyms: a language is always written in itself, so these are not dictionary entries. */
export const LOCALE_NAMES: Record<Locale, string> = { ru: 'Русский', en: 'English' };

const STORAGE_KEY = 'mylib-lang';

/**
 * The table is built from per-domain files that each prove both languages exist; this cast only
 * widens the literal tuples back to `Pair` so `t()` can read one row by key.
 */
const TABLE = STRINGS as Record<MsgKey, Pair>;

let current: Locale = 'ru';
const listeners = new Set<() => void>();

/** A stored choice always wins; otherwise Russian-speaking browsers get Russian, everyone else English. */
export function readLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'ru' || stored === 'en') return stored;
  return (navigator.language || 'ru').toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

export function getLocale(): Locale {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function render(value: Msg, vars: Vars): string {
  const text = typeof value === 'function' ? value(vars) : value;
  return text.replace(/\{(\w+)\}/g, (_match, name: string) => (name in vars ? String(vars[name]) : ''));
}

export function t(key: MsgKey, vars: Vars = {}): string {
  const pair = TABLE[key];
  return render(pair[current === 'ru' ? 0 : 1], vars);
}

function setMeta(attribute: 'name' | 'property', key: string, content: string): void {
  const tag = document.querySelector(`meta[${attribute}="${key}"]`);
  if (tag) tag.setAttribute('content', content);
}

/**
 * Writes the language into the document shell: `<html lang>` for screen readers and font matching,
 * the tab title and the share tags. index.html keeps the Russian copy because crawlers and the very
 * first paint are served from it — this runs before render, so nobody sees the swap.
 */
export function applyLocale(locale: Locale): void {
  current = locale;
  document.documentElement.lang = locale;
  document.title = t('meta.title');
  setMeta('name', 'description', t('meta.description'));
  setMeta('property', 'og:locale', locale === 'ru' ? 'ru_RU' : 'en_GB');
  setMeta('property', 'og:title', t('meta.title'));
  setMeta('property', 'og:description', t('meta.ogDescription'));
  setMeta('name', 'twitter:title', t('meta.title'));
  setMeta('name', 'twitter:description', t('meta.twitterDescription'));
}

export function writeLocale(locale: Locale): void {
  localStorage.setItem(STORAGE_KEY, locale);
  applyLocale(locale);
  for (const listener of listeners) listener();
}

/**
 * Screens read the language through the plain `t()` from this module, so only a component that
 * subscribes here refreshes when it changes. `App` does, which covers every route below it.
 */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale);
}
